/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreTimeNames - kTimeCore's day / month names, week numbers and time-from-now helpers.
 * Split out of kCoreTime.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kTimeCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils, serverLocale } from "./kCoreHandler";
import type { kTimeCore, kDateStyleOptions } from "./kCoreTime";

export class kCoreTimeNames {
    /** Full weekday name (e.g. "Monday", "måndag"). Pass a BCP-47 tag ("sv-SE") to force a
        locale; leave it empty for the host locale (serverLocale()). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.WeekdayName - its gate, try/catch and handler label "kTimeCore.WeekdayName" stay verbatim, so error reports are unchanged.
    public static WeekdayName = (Self: kTimeCore, Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Self.IsValid) { return ""; }
            return Self.ToJsDate().toLocaleDateString(Self["_UseLocale"](Locale), { weekday: "long" });
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.WeekdayName"); return ""; }
    };

    /**
     * The full written date with the weekday SPELLED OUT, in the locale's own word order —
     * "torsdag 26 september 2023" (sv-SE), "Tuesday, September 26, 2023" (en-US). This is the
     * `dateStyle: "full"` form; the weekday, month name, order and any punctuation all come from
     * the locale's CLDR data, so no month/weekday table is ever hand-rolled per project.
     * Pass a BCP-47 tag to force a locale, or leave it empty for the host locale.
     * Returns "" for an unset/1970 date.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToLongDate - its gate, try/catch and handler label "kTimeCore.ToLongDate" stay verbatim, so error reports are unchanged.
    public static ToLongDate = (Self: kTimeCore, Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Self.IsValid || Self.Year <= 1970) { return ""; }
            const Options: kDateStyleOptions = { dateStyle: "full" };
            return Self.ToJsDate().toLocaleDateString(Self["_UseLocale"](Locale), Options);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToLongDate"); return ""; }
    };

    /**
     * ToLongDate plus the time of day in the locale's short time form — "torsdag 26 september
     * 2023 kl. 14:32" (sv-SE), "Tuesday, September 26, 2023 at 2:32 PM" (en-US). The connecting
     * word ("kl.", "at", "um") is the locale's, not a literal.
     * Returns "" for an unset/1970 date.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToLongDateTime - its gate, try/catch and handler label "kTimeCore.ToLongDateTime" stay verbatim, so error reports are unchanged.
    public static ToLongDateTime = (Self: kTimeCore, Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Self.IsValid || Self.Year <= 1970) { return ""; }
            const Options: kDateStyleOptions = { dateStyle: "full", timeStyle: "short" };
            return Self.ToJsDate().toLocaleString(Self["_UseLocale"](Locale), Options);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToLongDateTime"); return ""; }
    };

    /** The caller's forced locale, or the host locale when the caller passed none. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore._UseLocale - its gate, try/catch and handler label "kTimeCore._UseLocale" stay verbatim, so error reports are unchanged.
    public static _UseLocale = (Self: kTimeCore, Locale: string): string => {
        if (getCoreUtils().KErrorMode) { return "en-US"; }
        try {
            if (Locale !== "") { return Locale; }
            return serverLocale();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore._UseLocale"); return "en-US"; }
    };

    /** Two-letter weekday abbreviation with first letter capitalised (e.g. "Mo"). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.WeekDayName2 - its gate, try/catch and handler label "kTimeCore.WeekDayName2" stay verbatim, so error reports are unchanged.
    public static WeekDayName2 = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Self.IsValid) { return ""; }
            const Short: string = Self.ToJsDate()
                .toLocaleDateString(serverLocale(), { weekday: "short" });
            return Short.substring(0, 1).toUpperCase() + Short.substring(1, 2);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.WeekDayName2"); return ""; }
    };

    /** Full month name (e.g. "January", "september"). Pass a BCP-47 tag ("sv-SE") to force a
        locale; leave it empty for the host locale (serverLocale()). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.MonthName - its gate, try/catch and handler label "kTimeCore.MonthName" stay verbatim, so error reports are unchanged.
    public static MonthName = (Self: kTimeCore, Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return Self.ToJsDate().toLocaleDateString(Self["_UseLocale"](Locale), { month: "long" });
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.MonthName"); return ""; }
    };

    // ── Calendar helpers ──────────────────────────────────────────────────────

    /** 0 = Sunday … 6 = Saturday (matches JS Date.getDay). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.Weekday - its gate, try/catch and handler label "kTimeCore.Weekday" stay verbatim, so error reports are unchanged.
    public static Weekday = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return -1; }
        try {
            return Self.ToJsDate().getDay();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.Weekday"); return -1; }
    };

    /** ISO 8601 week number (1-53). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.WeekNumber - its gate, try/catch and handler label "kTimeCore.WeekNumber" stay verbatim, so error reports are unchanged.
    public static WeekNumber = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const D: Date = Self["_IsoThursday"]();
            const Week1: Date = new Date(D.getFullYear(), 0, 4);
            return 1 + Math.round(
                ((D.getTime() - Week1.getTime()) / 86400000
                    - 3 + ((Week1.getDay() + 6) % 7)) / 7
            );
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.WeekNumber"); return 0; }
    };

    /** yyyyWW integer useful for grouping by year+week (e.g. 202403). The week is zero-padded
        and yyyy is the ISO 8601 WEEK-YEAR (the year of the week's Thursday), not the calendar
        year: 2024-12-30 gives 202501 and 2021-01-01 gives 202053 (owner decision 2026-10-09). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.YearWeekNumber - its gate, try/catch and handler label "kTimeCore.YearWeekNumber" stay verbatim, so error reports are unchanged.
    public static YearWeekNumber = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Self["_IsoThursday"]().getFullYear() * 100 + Self.WeekNumber();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.YearWeekNumber"); return 0; }
    };

    /** The Thursday of this date's ISO 8601 week, at local midnight: the ONE place the week-year rule lives (2026-10-09 H5; WeekNumber and
        YearWeekNumber each carried a copy, and the F10 bug was the two disagreeing about the year). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore._IsoThursday - its gate, try/catch and handler label "kTimeCore._IsoThursday" stay verbatim, so error reports are unchanged.
    public static _IsoThursday = (Self: kTimeCore): Date => {
        if (getCoreUtils().KErrorMode) { return new Date(0); }
        try {
            const Thursday: Date = Self.ToJsDate();
            Thursday.setHours(0, 0, 0, 0);
            Thursday.setDate(Thursday.getDate() + 3 - ((Thursday.getDay() + 6) % 7));
            return Thursday;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore._IsoThursday"); return new Date(0); }
    };

    // ── "From now" helpers ────────────────────────────────────────────────────

    /** Milliseconds since this point in time (positive = past, negative = future). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.MSecsFromNow - its gate, try/catch and handler label "kTimeCore.MSecsFromNow" stay verbatim, so error reports are unchanged.
    public static MSecsFromNow = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.round(new Date().getTime() - Self.ToJsDate().getTime());
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.MSecsFromNow"); return 0; }
    };

    /** Whole seconds since this point. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.SecondsFromNow - its gate, try/catch and handler label "kTimeCore.SecondsFromNow" stay verbatim, so error reports are unchanged.
    public static SecondsFromNow = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.round(
                Math.round(new Date().getTime() / 1000)
                - Self.ToJsDate().getTime() / 1000
            );
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SecondsFromNow"); return 0; }
    };

    /** Alias for SecondsFromNow (rounded from milliseconds). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.SecsFromNow - its gate, try/catch and handler label "kTimeCore.SecsFromNow" stay verbatim, so error reports are unchanged.
    public static SecsFromNow = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.round(Self.MSecsFromNow() / 1000);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SecsFromNow"); return 0; }
    };

    /** Whole minutes since this point. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.MinutesFromNow - its gate, try/catch and handler label "kTimeCore.MinutesFromNow" stay verbatim, so error reports are unchanged.
    public static MinutesFromNow = (Self: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.round(Self.SecondsFromNow() / 60);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.MinutesFromNow"); return 0; }
    };

    // ── Clone ─────────────────────────────────────────────────────────────────

    /** Return a deep copy of this kTimeCore. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.Clone - its gate, try/catch and handler label "kTimeCore.Clone" stay verbatim, so error reports are unchanged.
    public static Clone = (Self: kTimeCore, Make: () => kTimeCore): kTimeCore => {
        if (getCoreUtils().KErrorMode) { return Make(); }
        try {
            const T: kTimeCore = Make();
            T.Year = Self.Year; T.Month = Self.Month; T.Day = Self.Day;
            T.Hour = Self.Hour; T.Minute = Self.Minute; T.Second = Self.Second;
            T.MilliSecond = Self.MilliSecond; T.IsValid = Self.IsValid;
            return T;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.Clone"); return Make(); }
    };
}
